
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { BackgroundTaskHost, BackgroundTaskBuffer } from './BackgroundTaskHost';
import { IBackgroundTask, IBackgroundTaskQueue } from '../../models/backgroundTasks/IBackgroundTaskQueue';

/**
 * Utility which renders background tasks in a private DOM element.
 * Create an instance of this class and pass it to createItemCacheStore using the backgroundTaskQueueFacet.
 * @public
 */
export class BackgroundTaskRunner implements IBackgroundTaskQueue {
    private _backgroundTaskHelper: BackgroundTaskBuffer;
    private _taskCount: number;
    private _element: HTMLElement | null;

    constructor() {
        this._backgroundTaskHelper = new BackgroundTaskBuffer();
        this._taskCount = 0;
        this._element = null;
    }

    public enqueue(task: IBackgroundTask): void {
        // Invoke the async version.
        this._enqueue(task);
    }

    public stop(): void {
        if (this._element) {
            this._taskCount = 0;

            ReactDOM.unmountComponentAtNode(this._element);
            this._element.remove();
        }
    }

    private async _enqueue(task: IBackgroundTask): Promise<void> {
        if (this._taskCount === 0) {
            const element = this._element = document.createElement('div');
            element.style.display = 'none';
            element.classList.add('ms-ItemsView-backgroundTaskRunner');

            document.body.appendChild(element);

            ReactDOM.render(
                (
                    <BackgroundTaskHost queueRef={ this._backgroundTaskHelper.queueRef } />
                ),
                element
            );
        }

        this._taskCount++;

        this._backgroundTaskHelper.enqueue(task);

        try {
            await task.promise;
        } finally {
            // The task runner is still active, so this task should be counted.
            this._taskCount--;

            if (this._taskCount === 0 && this._element) {
                ReactDOM.unmountComponentAtNode(this._element);
                this._element.remove();
            }
        }
    }
}
